import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Home, Package, Info, LogOut, Settings, ClipboardList, Store, ChevronDown, ShieldCheck, Heart } from "lucide-react";

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    setIsLoggedIn(!!token);
    setUserRole(role);

    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("role");
    setIsLoggedIn(false);
    navigate("/signin");
  };

  const navLinks = [
    { to: "/home", icon: Home, label: "หน้าแรก" },
    { to: "/all-products", icon: Package, label: "สินค้า" },
    { to: "/about", icon: Info, label: "เกี่ยวกับ" },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? "bg-black/95 backdrop-blur-md shadow-lg" : "bg-black"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/home" className="flex items-center gap-2 text-xl font-bold text-white hover:text-red-500 transition-colors">
            <img src="/logo.webp" alt="IT Shop Logo" className="h-10 w-10 object-contain rounded-full" /> <span>IT Shop</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, icon: Icon, label }) => (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${location.pathname === to
                  ? "bg-red-600 text-white"
                  : "text-gray-200 hover:bg-gray-800 hover:text-white"
                  }`}
              >
                <Icon size={18} />
                {label}
              </Link>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {isLoggedIn ? (
              <>
                {/* Cart */}
                <Link
                  to="/my-cart"
                  className="p-2 text-gray-200 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ShoppingCart size={22} />
                </Link>

                {/* User Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-2 p-2 text-gray-200 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <User size={22} />
                    <ChevronDown size={16} className={`transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>

                  {dropdownOpen && (
                    <>
                      <div className="fixed inset-0" onClick={() => setDropdownOpen(false)} />
                      <div className="absolute right-0 mt-2 w-56 bg-gray-800 border border-gray-700 rounded-xl shadow-xl py-2 z-50">
                        <Link to="/account/profile" className="flex items-center gap-3 px-4 py-3 text-gray-200 hover:bg-gray-700 hover:text-white transition-colors" onClick={() => setDropdownOpen(false)}>
                          <User size={18} />
                          โปรไฟล์
                        </Link>
                        <Link to="/account/orders" className="flex items-center gap-3 px-4 py-3 text-gray-200 hover:bg-gray-700 hover:text-white transition-colors" onClick={() => setDropdownOpen(false)}>
                          <ClipboardList size={18} />
                          คำสั่งซื้อ
                        </Link>
                        <Link to="/account/wishlist" className="flex items-center gap-3 px-4 py-3 text-gray-200 hover:bg-gray-700 hover:text-white transition-colors" onClick={() => setDropdownOpen(false)}>
                          <Heart size={18} />
                          สินค้าที่ถูกใจ
                        </Link>

                        {userRole?.toUpperCase() === 'ADMIN' ? (
                          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 text-yellow-400 hover:bg-gray-700 hover:text-yellow-300 transition-colors" onClick={() => setDropdownOpen(false)}>
                            <ShieldCheck size={18} />
                            ศูนย์ Admin
                          </Link>
                        ) : (
                          <Link to="/account/my-store" className="flex items-center gap-3 px-4 py-3 text-gray-200 hover:bg-gray-700 hover:text-white transition-colors" onClick={() => setDropdownOpen(false)}>
                            <Store size={18} />
                            จัดการร้านค้า
                          </Link>
                        )}

                        <Link to="/account/settings" className="flex items-center gap-3 px-4 py-3 text-gray-200 hover:bg-gray-700 hover:text-white transition-colors" onClick={() => setDropdownOpen(false)}>
                          <Settings size={18} />
                          ตั้งค่า
                        </Link>
                        <hr className="my-2 border-gray-700" />
                        <button
                          onClick={() => { handleLogout(); setDropdownOpen(false); }}
                          className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-900/30 hover:text-red-300 transition-colors w-full"
                        >
                          <LogOut size={18} />
                          ออกจากระบบ
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/signin" className="px-4 py-2 text-gray-200 hover:text-white transition-colors">
                  เข้าสู่ระบบ
                </Link>
                <Link to="/signup" className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
                  สมัครสมาชิก
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 text-gray-200 hover:text-white"
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-gray-700 py-4">
            <nav className="flex flex-col gap-2">
              {navLinks.map(({ to, icon: Icon, label }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${location.pathname === to
                    ? "bg-red-600 text-white"
                    : "text-gray-200 hover:bg-gray-800"
                    }`}
                >
                  <Icon size={20} />
                  {label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
